import React, { useReducer, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BriefcaseIcon,
  PlusIcon,
  ArrowPathIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon, // Added for Edit
  TrashIcon,        // Added for Delete
  CurrencyRupeeIcon, // Added for CTC/Package
} from "@heroicons/react/24/outline";

/* -------------------------
  Shared constants & styles
------------------------- */
const COMPANY_NAME = "Acme Software Solutions Pvt Ltd";
const COMPANY_ADDRESS = "No. 22, Tech Park Road, Bengaluru, Karnataka – 560102";

// Utility to format date (replacing moment)
const formatDate = (dateString, format = "DD MMM YYYY") => {
  const date = dateString ? new Date(dateString) : new Date();
  
  if (format === "DD MMM YYYY") {
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }
  return date.toLocaleDateString();
};

const styles = {
  // 1. Background color changed to completely white
  page: { minHeight: "100vh", backgroundColor: "#ffffff", fontFamily: "'Poppins', 'Roboto', Arial, sans-serif" },
  container: { maxWidth: 1200, margin: "0 auto", padding: 20 },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e6e9ef", paddingBottom: 16, marginBottom: 18 },
  newButton: { 
    display: "inline-flex", 
    alignItems: "center", 
    gap: 8, 
    backgroundColor: "#2563eb", // Adjusted color for better enterprise feel (Blue 600)
    color: "white", 
    padding: "10px 18px", 
    borderRadius: 10, 
    border: "none", 
    cursor: "pointer", 
    fontWeight: 600,
    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
    transition: "background-color 0.2s"
  },
  card: { background: "white", padding: 18, borderRadius: 12, boxShadow: "0 6px 18px rgba(15,23,42,0.06)", border: "1px solid #eef2f7" },
};

// Available CTC Packages
const PACKAGE_BANDS = [
  { value: "500000", label: "₹5,00,000 (L1)" },
  { value: "800000", label: "₹8,00,000 (L2)" },
  { value: "1200000", label: "₹12,00,000 (L3)" },
  { value: "1800000", label: "₹18,00,000 (L4)" },
  { value: "Custom", label: "Custom Package" },
];

/* -------------------------
  initial state & reducer
------------------------- */
const initialState = {
  jobOffers: [],
  isLoading: true,
  isModalOpen: false,
  newOffer: {
    name: "",
    email: "",
    mobile: "",
    position: "",
    packageBand: "800000", // Default to L2 package
    ctc: "₹8,00,000", // Will be set by packageBand
    joiningDate: "",
    expiryDate: "",
    bond: "No",
    bondDuration: "",
    experienceType: "Fresher",
    experienceYears: "", // New field for years of experience
    employmentType: "Permanent", // Default changed from Company to Permanent
    terms: "Welcome to Acme Software Solutions. This offer is contingent upon successful background verification and acceptance of our employee handbook and policies.\n\nKey details:\n- Probation period: 6 months.\n- Leave entitlement: 20 days per annum.",
    document: null,
  },
};

function reducer(state, action) {
  switch (action.type) {
    case "FETCH_SUCCESS":
      return { ...state, jobOffers: action.payload, isLoading: false };
    case "TOGGLE_MODAL":
      return { 
        ...state, 
        isModalOpen: !state.isModalOpen,
        // Reset form when closing
        newOffer: state.isModalOpen ? initialState.newOffer : state.newOffer
      };
    case "UPDATE_FIELD":
      return { ...state, newOffer: { ...state.newOffer, [action.field]: action.value } };
      
    case "UPDATE_PACKAGE_BAND": {
      const selectedPackage = PACKAGE_BANDS.find(b => b.value === action.value);
      let ctcValue = action.value === "Custom" ? "" : formatCTC(action.value);
      return { ...state, newOffer: { ...state.newOffer, packageBand: action.value, ctc: ctcValue } };
    }
    
    case "UPDATE_EXPERIENCE_TYPE": {
        let years = action.value === "Fresher" ? "" : state.newOffer.experienceYears;
        return { ...state, newOffer: { ...state.newOffer, experienceType: action.value, experienceYears: years } };
    }
    
    case "ADD_OFFER": {
      const payload = {
        id: Date.now(),
        ...state.newOffer,
        status: "Offer Sent",
        // Use native Date formatting instead of moment
        createdOn: formatDate(new Date(), "YYYY-MM-DD"),
      };
      return { ...state, jobOffers: [payload, ...state.jobOffers], isModalOpen: false, newOffer: initialState.newOffer };
    }
    case "DELETE_OFFER":
        return { ...state, jobOffers: state.jobOffers.filter(o => o.id !== action.id) };
    case "UPDATE_STATUS":
      return { ...state, jobOffers: state.jobOffers.map(o => (o.id === action.id ? { ...o, status: action.status } : o)) };
    default:
      return state;
  }
}

/* -------------------------
  small helpers
------------------------- */
const formatCTC = (value) => {
  try {
    let s = String(value).replace(/[^\d]/g,"");
    if (!s) return "";
    // Format to Indian system (lakhs, thousands)
    let last3 = s.slice(-3);
    let rest = s.slice(0,-3);
    if (rest) rest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
    return `₹${rest ? rest + "," + last3 : last3}`;
  } catch { return value; }
};

const validateEmail = (email) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/* -------------------------
  Hook to load mock data
------------------------- */
const useJobOffers = () => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  
  useEffect(() => {
    // Mocking a successful database fetch with initial data
    const t = setTimeout(() => {
      dispatch({
        type: "FETCH_SUCCESS",
        payload: [
          {
            id: 1, name: "Ravi Kumar", email: "ravi@example.com", mobile: "9876543210",
            position: "Software Engineer", ctc: "₹8,00,000", joiningDate: "2025-10-01",
            expiryDate: "2025-09-28", bond: "No", experienceType: "Experienced", experienceYears: "4 years", employmentType: "Permanent",
            terms: "Standard employment terms apply.", status: "Offer Sent", createdOn: "2025-09-20"
          }
        ],
      });
    }, 500);
    return () => clearTimeout(t);
  }, []);
  return { state, dispatch };
};

/* -------------------------
  Component: Option 1
  SAME MODAL: form left + live preview right
------------------------- */
export default function JobOfferOption1() {
  const { state, dispatch } = useJobOffers();
  const { jobOffers, isLoading, isModalOpen, newOffer } = state;
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => { if (successMsg) { const t = setTimeout(()=>setSuccessMsg(""), 3000); return ()=>clearTimeout(t);} }, [successMsg]);

  const handleChange = (field, value) => {
    if (field === "mobile") value = String(value).replace(/\D/g, "").slice(0, 15);
    // Custom CTC input must be formatted
    if (field === "ctc" && newOffer.packageBand === "Custom") value = formatCTC(value);
    
    // Handle special fields
    if (field === "packageBand") {
        dispatch({ type: "UPDATE_PACKAGE_BAND", value });
        return;
    }
    if (field === "experienceType") {
        dispatch({ type: "UPDATE_EXPERIENCE_TYPE", value });
        return;
    }

    dispatch({ type: "UPDATE_FIELD", field, value });
  };
  
  const handleDelete = (id, name) => {
    // Simple confirmation replacement (no alert/confirm allowed)
    if (window.confirm(`Are you sure you want to delete the offer for ${name}?`)) {
      dispatch({ type: "DELETE_OFFER", id });
      setSuccessMsg(`❌ Offer for ${name} deleted.`);
    }
  };

  const validate = () => {
    const e = {};
    if (!newOffer.name.trim()) e.name = "Name is required";
    if (!validateEmail(newOffer.email)) e.email = "Valid email required";
    if (!newOffer.mobile || !/^\d{7,15}$/.test(newOffer.mobile)) e.mobile = "Enter valid mobile (7-15 digits)";
    if (!newOffer.position.trim()) e.position = "Designation required";
    if (!newOffer.ctc.trim()) e.ctc = "CTC is required. Enter value or select package.";
    if (!newOffer.joiningDate) e.joiningDate = "Joining date required";
    
    if (newOffer.experienceType === "Experienced" && !newOffer.experienceYears.trim()) {
        e.experienceYears = "Years of experience required for Experienced type.";
    }
    
    if (newOffer.bond === "Yes" && !newOffer.bondDuration.trim()) e.bondDuration = "Bond duration required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitOffer = (e) => {
    e.preventDefault();
    if (!validate()) return;
    dispatch({ type: "ADD_OFFER" });
    setSuccessMsg("✔️ Offer Letter Sent Successfully!");
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", background: "#ffffff" }}>
        <ArrowPathIcon style={{ height: 36, width: 36, color: "#2563eb", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
        <div style={{ marginTop: 12, color: "#4f46e5", fontWeight: 600 }}>Loading Offer Management System...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap" />
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <BriefcaseIcon style={{ height: 34, width: 34, color: "#2563eb" }} />
            <div style={{ fontSize: 24, fontWeight: 700, color: "#1f2937" }}>Offer Management</div>
          </div>
          <button onClick={() => dispatch({ type: "TOGGLE_MODAL" })} style={styles.newButton}>
            <PlusIcon style={{ height: 16, width: 16 }} /> New Offer
          </button>
        </div>

        {successMsg && (
            <motion.div initial={{opacity:0, y:-10}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-10}} 
                style={{padding: 12, background: successMsg.startsWith("✔️") ? "#dcfce7" : "#fee2e2", color: successMsg.startsWith("✔️") ? "#065f46" : "#ef4444", borderRadius: 8, marginBottom: 20, fontWeight: 600}}>
                {successMsg}
            </motion.div>
        )}

        {/* Pending offers table */}
        <div style={{ marginBottom: 20 }}>
          <div style={styles.card}>
            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 18, color: "#1f2937" }}>Pending Offers</h3>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 8px", fontSize: 14 }}>
                <thead style={{ background: "#f3f4f6", position: "sticky", top: 0, zIndex: 10 }}>
                  <tr style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                    <th style={{ textAlign: "left", padding: "12px 10px", borderRadius: "8px 0 0 8px" }}>Name</th>
                    <th style={{ textAlign: "left", padding: "12px 10px" }}>Email</th>
                    <th style={{ textAlign: "left", padding: "12px 10px" }}>Mobile</th>
                    <th style={{ textAlign: "left", padding: "12px 10px" }}>Designation</th>
                    <th style={{ textAlign: "left", padding: "12px 10px" }}>CTC</th>
                    <th style={{ textAlign: "left", padding: "12px 10px" }}>Experience</th>
                    <th style={{ textAlign: "left", padding: "12px 10px" }}>Employment</th>
                    <th style={{ textAlign: "left", padding: "12px 10px" }}>Bond</th>
                    <th style={{ textAlign: "left", padding: "12px 10px" }}>Status</th>
                    <th style={{ textAlign: "center", padding: "12px 10px", borderRadius: "0 8px 8px 0" }}>Actions</th> {/* Added Actions column */}
                  </tr>
                </thead>
                <tbody>
                  {jobOffers.length === 0 && (
                    <tr><td colSpan={10} style={{ padding: 14, textAlign: "center", color: "#6b7280" }}>No pending offers yet</td></tr>
                  )}
                  {jobOffers.map(o => (
                    <tr key={o.id} style={{ background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", borderRadius: 8 }}>
                      <td style={{ padding: 12, borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}>{o.name}</td>
                      <td style={{ padding: 12 }}>{o.email}</td>
                      <td style={{ padding: 12 }}>{o.mobile}</td>
                      <td style={{ padding: 12 }}>{o.position}</td>
                      <td style={{ padding: 12, fontWeight: 600, color: "#057a55" }}>{o.ctc}</td>
                      <td style={{ padding: 12 }}>
                        {o.experienceType}
                        {o.experienceType === "Experienced" && o.experienceYears && 
                          <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>({o.experienceYears})</span>
                        }
                      </td>
                      <td style={{ padding: 12 }}>{o.employmentType}</td>
                      <td style={{ padding: 12 }}>{o.bond}{o.bond === "Yes" && o.bondDuration ? ` (${o.bondDuration})` : ""}</td>
                      <td style={{ padding: 12 }}>
                        <span style={{ backgroundColor: "#dbeafe", color: "#1e40af", padding: "4px 8px", borderRadius: 4, fontWeight: 500, fontSize: 12 }}>
                          {o.status}
                        </span>
                      </td>
                      {/* New Actions column with icons */}
                      <td style={{ padding: 12, borderTopRightRadius: 8, borderBottomRightRadius: 8, display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button 
                          title="Edit Offer"
                          // In a real app, this would open an edit modal
                          onClick={() => alert(`Editing offer for ${o.name} (ID: ${o.id})`)} 
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#3b82f6", padding: 4 }}
                        >
                          <PencilSquareIcon style={{ height: 18, width: 18 }} />
                        </button>
                        <button 
                          title="Delete Offer"
                          onClick={() => handleDelete(o.id, o.name)} 
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: 4 }}
                        >
                          <TrashIcon style={{ height: 18, width: 18 }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modal: form left, preview right */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.7)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "50px 16px 16px 16px", zIndex: 9999 }}>
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: -20 }} 
                animate={{ scale: 1, opacity: 1, y: 0 }} 
                exit={{ scale: 0.9, opacity: 0, y: -20 }} 
                // Reduced overall modal height
                style={{ width: "100%", maxWidth: 1100, borderRadius: 12, overflow: "hidden", background: "white", boxShadow: "0 12px 40px rgba(2,6,23,0.3)", height: "calc(100vh - 80px)", marginTop: 0 }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 16, background: "#2563eb", color: "white" }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Create New Offer Letter</h3>
                  <button onClick={() => dispatch({ type: "TOGGLE_MODAL" })} style={{ background: "transparent", border: "none", color: "white", cursor: "pointer" }} title="Close"><XMarkIcon style={{ height: 22, width: 22 }} /></button>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", height: "calc(100% - 56px)" }}>
                  {/* form left */}
                  <div style={{ width: "100%", maxWidth: 480, padding: 24, borderRight: "1px solid #f3f4f6", maxHeight: "100%", overflowY: "auto" }}>
                    <form onSubmit={submitOffer} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      
                      {/* Name, Email, Mobile, Position fields remain the same structure */}
                      {["name", "email", "mobile", "position"].map(field => (
                          <div key={field}>
                              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>{field === 'name' ? 'Full Name' : field === 'position' ? 'Designation' : field.charAt(0).toUpperCase() + field.slice(1)} *</label>
                              <input 
                                value={newOffer[field]} 
                                onChange={(e) => handleChange(field, e.target.value)} 
                                inputMode={field === 'mobile' ? "numeric" : "text"}
                                style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${errors[field] ? '#ef4444' : '#d1d5db'}`, transition: 'border-color 0.2s' }} />
                              {errors[field] && <div style={{ color: "#dc2626", marginTop: 6, fontSize: 12 }}>{errors[field]}</div>}
                          </div>
                      ))}

                      {/* CTC (Package Dropdown + Custom Input) */}
                      <div>
                        <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Compensation (CTC) *</label>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {/* Package Dropdown */}
                          <select 
                            value={newOffer.packageBand} 
                            onChange={(e) => handleChange("packageBand", e.target.value)} 
                            style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db", flexShrink: 0, minWidth: 150 }}
                          >
                            {PACKAGE_BANDS.map(band => (
                              <option key={band.value} value={band.value}>{band.label}</option>
                            ))}
                          </select>
                          
                          {/* Custom CTC Input */}
                          {newOffer.packageBand === "Custom" && (
                            <div style={{ flexGrow: 1, position: 'relative' }}>
                                <CurrencyRupeeIcon style={{ height: 18, width: 18, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                                <input 
                                  value={newOffer.ctc} 
                                  onChange={(e) => handleChange("ctc", e.target.value)} 
                                  placeholder="Enter custom CTC (e.g., 1500000)" 
                                  style={{ width: "100%", padding: "10px 10px 10px 35px", borderRadius: 8, border: `1px solid ${errors.ctc ? '#ef4444' : '#d1d5db'}` }} 
                                />
                            </div>
                          )}
                          {newOffer.packageBand !== "Custom" && (
                            <div style={{ padding: 10, flexGrow: 1, color: '#4b5563' }}>Selected: <span style={{fontWeight: 700}}>{newOffer.ctc || 'N/A'}</span></div>
                          )}
                        </div>
                        {errors.ctc && <div style={{ color: "#dc2626", marginTop: 6, fontSize: 12 }}>{errors.ctc}</div>}
                      </div>

                      {/* Experience Type and Years */}
                      <div style={{ display: "flex", gap: 16 }}>
                        {/* Experience Type Dropdown */}
                        <div style={{ flex: newOffer.experienceType === "Experienced" ? 0.5 : 1 }}>
                          <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Candidate Experience *</label>
                          <select value={newOffer.experienceType} onChange={(e)=>handleChange("experienceType", e.target.value)} style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db", width: "100%" }}>
                            <option>Fresher</option>
                            <option>Experienced</option>
                          </select>
                        </div>
                        
                        {/* Conditional Experience Years Input */}
                        {newOffer.experienceType === "Experienced" && (
                          <div style={{ flex: 0.5 }}>
                            <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Years of Experience *</label>
                            <input 
                                placeholder="e.g. 3.5 years" 
                                value={newOffer.experienceYears} 
                                onChange={(e) => handleChange("experienceYears", e.target.value)} 
                                style={{ padding: 10, borderRadius: 8, border: `1px solid ${errors.experienceYears ? '#ef4444' : '#d1d5db'}`, flex: 1, width: "100%" }} />
                            {errors.experienceYears && <div style={{ color: "#dc2626", marginTop: 6, fontSize: 12 }}>{errors.experienceYears}</div>}
                          </div>
                        )}
                      </div>

                      {/* Employment Type */}
                      <div>
                        <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Employment Type *</label>
                        <select value={newOffer.employmentType} onChange={(e)=>handleChange("employmentType", e.target.value)} style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db", width: "100%" }}>
                          <option>Permanent</option> {/* Changed from Company to Permanent */}
                          <option>Contract</option>
                          <option>Internship</option>
                        </select>
                      </div>

                      {/* Joining and Expiry Date */}
                      <div style={{ display: "flex", gap: 16 }}>
                        <div>
                          <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Joining Date *</label>
                          <input type="date" value={newOffer.joiningDate} onChange={(e) => handleChange("joiningDate", e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${errors.joiningDate ? '#ef4444' : '#d1d5db'}` }} />
                          {errors.joiningDate && <div style={{ color: "#dc2626", marginTop: 6, fontSize: 12 }}>{errors.joiningDate}</div>}
                        </div>
                        <div>
                          <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Offer Expiry Date</label>
                          <input type="date" value={newOffer.expiryDate} onChange={(e) => handleChange("expiryDate", e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }} />
                        </div>
                      </div>

                      {/* Bond (Dropdown + Duration Input) */}
                      <div>
                        <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Service Bond Applicable?</label>
                        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                          <select value={newOffer.bond} onChange={(e) => handleChange("bond", e.target.value)} style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}>
                            <option>No</option>
                            <option>Yes</option>
                          </select>
                          {newOffer.bond === "Yes" && (
                            <input 
                                placeholder="Duration (e.g. 1 year)" 
                                value={newOffer.bondDuration} 
                                onChange={(e) => handleChange("bondDuration", e.target.value)} 
                                style={{ padding: 10, borderRadius: 8, border: `1px solid ${errors.bondDuration ? '#ef4444' : '#d1d5db'}`, flex: 1 }} />
                          )}
                        </div>
                        {errors.bondDuration && <div style={{ color: "#dc2626", marginTop: 6, fontSize: 12 }}>{errors.bondDuration}</div>}
                      </div>

                      {/* Terms (Textarea) */}
                      <div>
                        <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Custom Terms & Conditions</label>
                        <textarea value={newOffer.terms} onChange={(e)=>handleChange("terms", e.target.value)} style={{ width: "100%", minHeight: 120, padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }} />
                      </div>

                      {/* File Upload (Renamed) */}
                      <div>
                        <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: 'block', marginBottom: 4 }}>Offer Document: Terms & Policy Upload</label> {/* Renamed */}
                        <input type="file" accept=".pdf,.doc,.docx" onChange={(e)=>handleChange("document", e.target.files[0])} style={{ padding: '8px 0' }} />
                        <p style={{ marginTop: 8, fontSize: 12, color: '#10b981', fontWeight: 600, padding: 8, background: '#ecfdf5', borderRadius: 6 }}>
                           💡 Auto-Read T&C: Document will be processed automatically upon upload to extract standard terms for review and quick finalization. (Enterprise Feature Placeholder)
                        </p> {/* Auto-read feature placeholder */}
                      </div>

                      {/* Submit Button */}
                      <div>
                        <button type="submit" style={{ width: "100%", padding: 14, background: "#2563eb", color: "white", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 16, marginTop: 8, boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)", transition: "background-color 0.2s" }}>
                          Send Offer Letter
                        </button>
                      </div>

                    </form>
                  </div>

                  {/* preview right */}
                  <div style={{ flex: 1, padding: 24, background: "#f9fafb", maxHeight: "100%", overflowY: "auto" }}>
                    <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #e5e7eb", paddingBottom: 10 }}>
                      <h4 style={{ margin: 0, fontSize: 18, color: "#1f2937", fontWeight: 700 }}>Offer Preview</h4>
                      <div style={{ color: "#6b7280", fontSize: 14, fontWeight: 500 }}>{formatDate()}</div>
                    </div>

                    <div style={{ ...styles.card, background: "#ffffff", border: "1px solid #d1d5db", padding: 20 }}>
                      <h5 style={{ fontWeight: 800, marginBottom: 8, fontSize: 18, color: "#1f2937" }}>{COMPANY_NAME}</h5>
                      <p style={{ marginTop: 0, color: "#6b7280", fontSize: 12, marginBottom: 16 }}>{COMPANY_ADDRESS}</p>

                      <div style={{ marginTop: 16, borderTop: "1px dashed #e5e7eb", paddingTop: 16 }}>
                        <p style={{ margin: "8px 0", fontSize: 15 }}><strong>Candidate:</strong> {newOffer.name || "Candidate Name"}</p>
                        <p style={{ margin: "8px 0", fontSize: 15 }}><strong>Email:</strong> {newOffer.email || "-"}</p>
                        <p style={{ margin: "8px 0", fontSize: 15 }}><strong>Mobile:</strong> {newOffer.mobile || "-"}</p>
                        <p style={{ margin: "8px 0", fontSize: 15 }}><strong>Designation:</strong> <strong style={{color: "#2563eb"}}>{newOffer.position || "-"}</strong></p>
                        <p style={{ margin: "8px 0", fontSize: 15 }}><strong>Annual CTC:</strong> <strong style={{color: "#057a55"}}>{newOffer.ctc || "-"}</strong></p>
                        <p style={{ margin: "8px 0", fontSize: 15 }}><strong>Joining Date:</strong> <strong>{newOffer.joiningDate || "-"}</strong></p>
                        
                        <div style={{ display: "flex", gap: 20, marginTop: 12, padding: 10, background: '#f3f4f6', borderRadius: 8 }}>
                            <p style={{ margin: 0, fontSize: 15 }}><strong>Experience:</strong> {newOffer.experienceType} {newOffer.experienceType === "Experienced" && newOffer.experienceYears && `(${newOffer.experienceYears})`}</p>
                            <p style={{ margin: 0, fontSize: 15 }}><strong>Employment Type:</strong> {newOffer.employmentType}</p>
                            <p style={{ margin: 0, fontSize: 15 }}><strong>Bond:</strong> {newOffer.bond}{newOffer.bond === "Yes" && newOffer.bondDuration ? ` (${newOffer.bondDuration})` : ""}</p>
                        </div>


                        <div style={{ marginTop: 20 }}>
                          <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 16, color: "#1f2937" }}>Detailed Terms & Conditions:</p>
                          <div style={{ whiteSpace: "pre-wrap", background: "#f0f9ff", padding: 15, borderRadius: 8, borderLeft: "4px solid #38bdf8", fontSize: 14, lineHeight: 1.6 }}>{newOffer.terms || "No additional terms provided."}</div>
                        </div>

                        <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
                          <button 
                            // Using a cleaner button style
                            onClick={()=>alert("Download feature is ready. (Actual download functionality needs server-side implementation)")} 
                            style={{ 
                              padding: "10px 16px", 
                              border: "1px solid #3b82f6", 
                              background: "#eff6ff", 
                              color: "#3b82f6", 
                              borderRadius: 8,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}> 
                            <ArrowDownTrayIcon style={{ height: 16, width: 16 }} /> Download Offer
                          </button>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}